import {
  ClipboardCopy,
  ErrorDialog,
  Modal,
  Skeleton,
  Tab,
  TabPanel,
  Tabs,
  Typography,
} from "@codat/orchard-ui";
import { useGetSyncOverview } from "api/public";
import React, { useState } from "react";
import { useCopyToClipboard } from "utils/copyToClipboard";
import formatUrl from "utils/formatUrl";

import { paths } from "components/App/paths";
import { Button } from "components/Button/Button";

import { StatusPill } from "../StatusPill";
import styles from "./SyncOverviewModal.module.scss";

interface SyncModalProps {
  isOpen: boolean;
  clientId: string;
  syncId: string;
  onClose: () => void;
}

const Field: React.FC<{ label: string; value: string; canCopy?: boolean }> = ({
  label,
  value,
  canCopy,
}) => {
  const copyToClipboard = useCopyToClipboard();
  return (
    <>
      <Typography variant="body_md" as="p">
        {label}
      </Typography>
      <div className={styles.fieldContainer}>
        <Typography variant="body_md" as="p" className={styles.truncateField}>
          {value}
        </Typography>
        {canCopy && (
          <ClipboardCopy
            text={value}
            noBackground
            onCopy={() => copyToClipboard(label, value)}
            color="interface"
          />
        )}
      </div>
    </>
  );
};

export const SyncOverviewModal: React.FC<SyncModalProps> = ({
  clientId,
  syncId,
  onClose,
}) => {
  const [{ loading, data, error }, refetch] = useGetSyncOverview(
    clientId,
    syncId
  );

  const [selectedTabId, setSelectedTabId] = useState("summary");

  const handleTabChange = (
    e: React.MouseEvent<HTMLButtonElement>,
    value: string
  ) => {
    e.preventDefault();
    setSelectedTabId(value);
  };

  const copyToClipboard = useCopyToClipboard();

  const getErrorMessage = (errorMessage: string, errorCode: string) => {
    return errorCode.startsWith("4")
      ? errorMessage
      : "An internal error occurred - please contact Codat support.";
  };

  return (
    <Modal onClose={onClose} size="medium" data-testid="sync-overview-modal">
      <div className={styles.modalContainer}>
        <div style={{ position: "relative" }}>
          <Modal.Header title="Sync overview" onClose={onClose} />
          {data && (
            <StatusPill value={data.status} className={styles.statusTopRight} />
          )}
        </div>
        <div className={styles.modalContent}>
          <Tabs
            label="Summary"
            selectedTabId={selectedTabId}
            onTabChange={handleTabChange}
          >
            <Tab
              id="summary"
              label="Summary"
              aria-controls="tabpanel-summary"
            />
          </Tabs>
          {loading ? (
            <div className={styles.skeletonPadding}>
              <Skeleton width="43.125rem" height="36.875rem" />
            </div>
          ) : error || data === undefined ? (
            <ErrorDialog
              correlationId={error?.response?.data.correlationId}
              statusCode={error ? Number(error?.code) : 500}
            />
          ) : (
            <div className={styles.panelContainer}>
              <TabPanel tabId="summary" selectedTabId={selectedTabId}>
                <Modal.Body variant="light">
                  <div className={styles.overviewFields}>
                    <Field
                      label="Company Name"
                      value={data.companyName}
                      canCopy
                    />
                    <Field label="Company ID" value={data.companyId} canCopy />
                    <Field
                      label="Accounting Data Connection ID"
                      value={data.dataConnectionId}
                      canCopy
                    />
                    <Field
                      label="Source Account ID"
                      value={data.sourceAccountId}
                      canCopy
                    />
                    <Field
                      label="Sync Period"
                      value={`${data.periodStartUtc} - ${data.periodEndUtc}`}
                    />
                    <Field
                      label="Execution Start UTC"
                      value={data.executionStartUtc}
                    />
                    <Field
                      label="Execution End UTC"
                      value={data.executionEndUtc || "-"}
                    />
                  </div>
                </Modal.Body>
                <Modal.Body variant="dark">
                  <div
                    className={styles.overviewDetails}
                    data-testid="overview-details"
                  >
                    <Typography variant="heading_md" as="p">
                      Details
                    </Typography>
                    {data.errorCode || data.errorMessage ? (
                      <>
                        <div className={styles.errorCode}>
                          <Typography variant="heading_sm" as="h3">
                            Error code:
                          </Typography>
                          <Typography variant="body_md" as="p">
                            {data.errorCode}
                          </Typography>
                          <ClipboardCopy
                            text={data.errorCode}
                            noBackground
                            onCopy={() =>
                              copyToClipboard("Error code", data.errorCode)
                            }
                            color="interface"
                          />
                        </div>
                        <div className={styles.errorMessage}>
                          <Typography variant="heading_sm" as="h3">
                            Error message:
                          </Typography>
                          <Typography variant="body_md" as="p">
                            {getErrorMessage(data.errorMessage, data.errorCode)}
                          </Typography>
                        </div>
                      </>
                    ) : (
                      <Typography as="p">{data.status}</Typography>
                    )}
                    {data.pushOperationKeys &&
                      data.pushOperationKeys.length > 0 && (
                        <div className={styles.pushOperationKeys}>
                          <div className={styles.pushOperationKeysHeader}>
                            <Typography variant="heading_sm" as="h3">
                              Push Operation Keys:
                            </Typography>
                            <Button
                              href={formatUrl(paths.companies.WRITE_HISTORY, {
                                companyId: data.companyId,
                              })}
                              variant="secondary"
                              color="action"
                              size="small"
                              buttonName="View push history"
                              className={styles.pushHistoryLink}
                            >
                              View push history
                            </Button>
                          </div>
                          <div className={styles.keysList}>
                            {data.pushOperationKeys.map((key, index) => (
                              <div key={index} className={styles.keyItem}>
                                <Typography as="p">{key}</Typography>
                                <ClipboardCopy
                                  text={key}
                                  noBackground
                                  onCopy={() =>
                                    copyToClipboard("Push Operation Key", key)
                                  }
                                  color="interface"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>
                </Modal.Body>
              </TabPanel>
            </div>
          )}
        </div>
        <Modal.Footer
          variant="light"
          buttons={[
            {
              label: "Refresh",
              variant: "secondary",
              color: "action",
              type: "button",
              onClick: () => refetch(),
            },
            {
              label: "Close",
              variant: "secondary",
              color: "interface",
              type: "button",
              onClick: onClose,
            },
          ]}
          leftContent={
            data && (
              <div className={styles.modalFooter}>
                <Typography variant="body_md" as="p">
                  {`Sync ID: ${data.syncId}`}
                </Typography>
                <ClipboardCopy
                  text={data.syncId}
                  noBackground
                  onCopy={() => copyToClipboard("Sync ID", data.syncId)}
                  color="interface"
                />
              </div>
            )
          }
        />
      </div>
    </Modal>
  );
};
